import React, { useContext, useState } from 'react';
import { gql, useQuery, useMutation } from '@apollo/client';
import { View, Text, Pressable } from 'react-native';
import styled from 'styled-components/native';
import DataTable from '@preflighttech/preflight-tables';
import moment from 'moment';
import FileDownload from 'js-file-download';

import AuthContext from 'app/src/contexts/AuthContext';
import NewCustomerRefund from 'app/src/components/Accounting/CustomerRefunds/new';
import DataState from 'app/src/components/DataState';
import ResourceNotifications from 'app/src/components/Notifications/Resource';
import Modal from 'app/src/elements/Modal';
import Print from 'app/src/elements/Print';
import minimalTable from 'app/src/elements/minimalTable';
import { Link } from 'app/src/utils/routing';
import currency from 'app/src/utils/currencyFormatter';
import {
  SALE_ASSET_CORE_FIELDS,
  OFFER_FIELDS
} from 'app/src/components/Sales/Process/queries';
import { dateShort } from 'app/src/utils/timeFormatter';
import { rest } from 'app/src/utils/rest';

import { UNWIND_SOLD_ASSET } from './queries';

const date = value => {
  if (!value) { return ''; }

  return moment(value).format('M/D/YY');
};

// Not defining in ./queries to avoid imports loop
const ASSET_SALE_ASSETS = gql`
  ${SALE_ASSET_CORE_FIELDS}
  ${OFFER_FIELDS}
  query AssetSaleAssets(
    $id: ID!
    $saleAssetId: ID
  ) {
    asset(id: $id) {
      sellerFeesTotal
      vin
      saleAssets(id: $saleAssetId) {
        ...SaleAssetCoreFields
        soldAmount
        buyerNet
        buyerFees
        sale { id, name, saleOn }
        offers {
          ...OfferFields
          isOriginal
        }
        soldOffer {
          ...OfferFields
        }
        saleHistoryDeltas {
          id
          userDescription
          createdAt
          createdTimestamp
          saleHistoryDescription
        }
        saleHistoryComments {
          id
          userDescription
          createdAt
          createdTimestamp
          body
        }
        customerRefunds {
          id
          bankDate
          amount
        }
      }
    }
  }
`;

const SaleAssetContainer = styled(View)`
  margin: 10px;
`;

const SaleName = styled(Text)`
  font-weight: 600;
`;

const Offers = styled(View)`
  margin: 5px;
  padding: 5px;
  background-color: white;
  border-width: 1px;
  border-color: blank;
`;

const Field = styled(View)`
  flex-direction: row;
  gap: 10px;
`;

const Label = styled(Text)`
  min-width: 100px;
`;

const columns = [
  {
    key: 'date',
    label: 'Date',
    sort: 'prevent',
    width: 90,
    content: ({ entry }) => date(entry.createdAt),
  },
  {
    key: 'time',
    label: 'Time',
    sort: 'prevent',
    width: 90,
    content: ({ entry }) => moment(entry.createdAt).format('h:mm A'),
  },
  {
    key: 'userDescription',
    label: 'User',
    sort: 'prevent',
    width: "25%",
  },
  {
    key: 'deltaDescription',
    label: 'Action',
    sort: 'prevent',
  },
];

const SaleAsset = ({ asset, saleAsset, refetch }) => {
  const [showAddCustomerRefundModal, setShowAddCustomerRefundModal] =
    useState(false);

  const { user, credentials, hasPolicy } = useContext(AuthContext);

  const [unwindSoldAsset] = useMutation(UNWIND_SOLD_ASSET, {
    onCompleted: refetch
  });

  const { sale, offers } = saleAsset;

  const saleName = sale ? sale.name : 'Sold Direct';

  const deltas = [];

  saleAsset.saleHistoryDeltas.forEach(delta => {
    const deltaObject = { ...delta };

    deltaObject.deltaDescription = deltaObject.saleHistoryDescription;

    if (deltaObject.deltaDescription) {
      deltas.push(deltaObject);
    }
  });

  saleAsset.saleHistoryComments.forEach(comment => {
    const { body, ...deltaObject } = comment;
    deltaObject.deltaDescription = `Comment: ${body}`;
    deltas.push(deltaObject);
  });

  const sortedDeltas = deltas
    .sort((a, b) => a.createdTimestamp - b.createdTimestamp);

  let releaseStatus;

  if (saleAsset.releaseConfirmed) {
    releaseStatus = 'Release Confirmed';
  } else if (saleAsset.release) {
    releaseStatus = 'Release Sent';
  }

  const buyer = saleAsset.soldOffer?.buyer;

  const downloadBOS = type => {
    const options = {
      credentials,
      params: { 'sale-asset-id': saleAsset.id, type },
      path: 'download-bos',
      headers: { Accept: 'application/pdf' }
    };

    rest(options).then(response => {
      response.blob().then(blob => (
        FileDownload(blob, `${asset.vin}-bos-${type}.pdf`)
      ));
    });
  };

  const canUnwind =
    ['Sold', 'Sold Direct'].includes(saleAsset.status) &&
    hasPolicy({ type: 'SaleAsset', action: 'destroy' })

  const canRemove = ['Pending', 'Listed'].includes(saleAsset.status) &&
    hasPolicy({ type: 'SaleAsset', action: 'destroy' })

  const handleUnwind = () => {
    if (confirm('Really unwind the sale of this asset?')) {
      unwindSoldAsset({ variables: { id: saleAsset.id } });
    }
  };

  const handleRemove = () => {
    if (confirm(`Really remove this asset from ${saleName}?`)) {
      // unwindSoldAsset mutation handles whether to unwind or just remove
      unwindSoldAsset({ variables: { id: saleAsset.id } });
    }
  };

  return (
    <SaleAssetContainer>
      <Field>
        <Link to={`/sales/${sale.id}`}>
          <SaleName>{saleName}</SaleName>
        </Link>
        <ResourceNotifications resource={saleAsset} />
        <Print
          path={`reports/sale-history/${saleAsset.id}`}
          filename={`bid-history-${asset.vin}`}
        />
      </Field>
      <Field>
        <Label>Lot Number:</Label>
        <Text>{saleAsset.correctedLotNumber}</Text>
      </Field>
      <Field>
        <Label>Status:</Label>
        <Text>
          {saleAsset.status}{' '}
          {saleAsset.soldAmount && currency(saleAsset.soldAmount)}
        </Text>
        {canUnwind && (
          <Pressable onPress={handleUnwind}>
            <Text>Unwind Sale</Text>
          </Pressable>
        )}
        {canRemove && (
          <Pressable onPress={handleRemove}>
            <Text>Remove From Sale</Text>
          </Pressable>
        )}
      </Field>
      <Field>
        <Label>Seller:</Label>
        <Text>
          Fees: {currency(asset.sellerFeesTotal)}
        </Text>
        {saleAsset.soldAmount && 'Removed' !== saleAsset.status && (
          <>
            <Text>Seller Net: {currency(saleAsset.sellerNet)}</Text>
            <Text>
              Date of ACH: {date(saleAsset.sellerAchOn)}
            </Text>
            <Pressable onPress={() => downloadBOS('seller')}>
              <Text>Download BOS</Text>
            </Pressable>
          </>
        )}
      </Field>
      {buyer && (
        <Field>
          <Label>{buyer.name}:</Label>
          <Text>
            Fees: {currency(saleAsset.buyerFees)}
          </Text>
          {saleAsset.soldAmount && 'Removed' !== saleAsset.status && (
            <>
              <Text>
                Buyer Total: {currency(saleAsset.buyerNet)}
              </Text>
              <Text>
                Buyer Invoice:
                {' '}{saleAsset.invoiceNumber}
                {' '}{date(saleAsset.invoiceSentOn)}
              </Text>
              <Text>
                Date of Wire: {date(saleAsset.buyerWireOn)}
              </Text>
              <Pressable onPress={() => downloadBOS('buyer')}>
                <Text>Download BOS</Text>
              </Pressable>
              {0 === saleAsset.customerRefunds.length ? (
                <Pressable onPress={() => setShowAddCustomerRefundModal(true)}>
                  <Text>Refund Customer</Text>
                </Pressable>
              ) : (
                <View style={{ flexDirection: 'row' }}>
                  <Text style={{ fontWeight: 600, color: 'darkred'}}>
                    Customer has been refunded
                  </Text>

                  {saleAsset.customerRefunds.map(refund => (
                    <Link
                      key={refund.id}
                      to={`/accounting/transactions/${refund.id}`}
                    >
                      <Text key={refund.id} style={{ marginLeft: 4 }}>
                        {dateShort(refund.bankDate)} - View{'  '}
                      </Text>
                    </Link>
                  ))}
                </View>
              )}
            </>
          )}
        </Field>
      )}
      {releaseStatus && (
        <Text>{releaseStatus}</Text>
      )}
      {deltas.length > 0 && (
        <DataTable.Simple
          data={sortedDeltas}
          columns={columns}
          component={minimalTable}
          pageLength="All"
        />
      )}

      {showAddCustomerRefundModal && (
        <Modal
          setShowModal={setShowAddCustomerRefundModal}
          modalType="Add Customer Refund"
        >
          <NewCustomerRefund
            asset={asset}
            saleAsset={saleAsset}
            setShowModal={setShowAddCustomerRefundModal}
            refetch={refetch}
          />
        </Modal>
      )}
    </SaleAssetContainer>
  );
};

const SaleHistory = ({ asset, saleAsset, refetchAsset }) => {
  const [showRemoved, setShowRemoved] = useState(!!saleAsset);

  const { data, loading, error, refetch } = useQuery(ASSET_SALE_ASSETS, {
    variables: { id: asset.id, saleAssetId: saleAsset?.id },
    fetchPolicy: 'no-cache',
  });

  // The header has its own ASSET query; changes here must refresh it too.
  const refetchAll = () => {
    refetch();
    refetchAsset?.();
  };

  if (!data) { return <DataState.Loading /> }

  const saleAssets = data.asset.saleAssets;

  if (0 === saleAssets.length) {
    return (<Text>Not listed in any sales</Text>);
  }

  const shownSaleAssets = saleAssets.filter(({ status }) => (
    showRemoved || 'Removed' !== status
  ));

  const removedSaleAssets = saleAssets.filter(({ status }) => (
    'Removed' === status
  ));

  return (
    <View>
      {!showRemoved && removedSaleAssets.length > 0 && (
        <SaleAssetContainer>
          <Pressable onPress={() => setShowRemoved(true)}>
            <Text>
              Removed from {removedSaleAssets.length}
              {1 === removedSaleAssets.length ? ' Sale' : ' Sales'}.
              Click to show details.
            </Text>
          </Pressable>
        </SaleAssetContainer>
      )}
      {shownSaleAssets.map(item => (
        <SaleAsset
          key={item.id}
          saleAsset={item}
          asset={{ ...data.asset, id: asset.id }}
          refetch={refetchAll}
        />
      ))}
    </View>
  );
};

export default SaleHistory;
